import dayjs from 'dayjs';
import React, { useLayoutEffect, useMemo } from 'react';
import { Col, Image, Row } from 'react-bootstrap';
import LazyLoad from 'react-lazyload';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import {
  IMAGE_ATTRIBUTE, LINK_GOOGLE_NEWS, URL_IMAGE_GOOGLE_NEWS
} from '../../utils/constants';

import Products from './components';
import { NewsDetailBlocks } from './types';

import HelmetContainer from 'common/Helmet';
// import Image from 'components/atoms/Image';
import LoadingPage from 'common/LoadingPage';
import Icon from 'components/atoms/Icon';
import Link from 'components/atoms/Link';
import Paragraph from 'components/atoms/Paragraph';
import Breadcrumb from 'components/molecules/Breadcrumb';
import NewsCard from 'components/molecules/NewsCard';
import Container from 'components/organisms/Container';
import NotFound from 'pages/NotFound';
import { pageService } from 'services/pages';
import { getPostBySlugService } from 'services/post';
import { useAppSelector } from 'store/hooks';
import { formatDataPost } from 'utils/formatData';
import { baseURL, checkSeoData } from 'utils/functions';

const Screen = () => {
  const { static: links } = useAppSelector((state) => state.systems);
  const { slug } = useParams<{ slug: string }>();

  const { data: stateDetail, isLoading, isError: isErrorDetail } = useQuery(
    [`NEWS_DETAIL_${slug}`, { slug }],
    () => getPostBySlugService(slug),
    { enabled: !!slug }
  );

  const { data: statePage } = useQuery(
    ['NEWS_DETAIL_PAGE_DATA', { newsDetail: links?.newsDetail }],
    () => pageService<NewsDetailBlocks>(links?.newsDetail),
    { enabled: !!links?.newsDetail }
  );

  useLayoutEffect(() => { window.history.scrollRestoration = 'manual'; }, []);

  const fullBreadcrumbs = useMemo(() => {
    if (statePage && statePage?.breadcrumbs) {
      return [...statePage.breadcrumbs, {
        text: stateDetail?.title,
        slug: `tin-tuc/${stateDetail?.slug}`
      }];
    }
    return [];
  }, [statePage, stateDetail]);
  const listRelated = useMemo(
    () => stateDetail?.related?.map((item) => formatDataPost(item)) || [],
    [stateDetail]
  );

  const detail = useMemo(() => {
    let newContent = stateDetail?.content;
    if (newContent !== undefined && newContent !== null) {
      newContent = newContent.replaceAll('<img', '<img loading="lazy"');
      newContent = newContent.replaceAll(IMAGE_ATTRIBUTE, `src="${baseURL('/')}storage/upload/media`);
    }

    return {
      title: stateDetail?.title,
      time: dayjs(stateDetail?.publishedAt).format('DD/MM/YYYY'),
      srcImage: baseURL(stateDetail?.thumbnail.path),
      content: newContent
    };
  }, [stateDetail]);

  // const hashtagList = useMemo(() => stateDetail?.hashtags.map((item) => ({
  //   title: item.title,
  //   slug: item.slug,
  //   id: item.id
  // })), [stateDetail]);

  const seoData = useMemo(
    () => {
      let seoDataCustom = stateDetail?.seoData;
      if (checkSeoData(seoDataCustom)) {
        return seoDataCustom;
      }
      seoDataCustom = { ...statePage?.seoData };
      if (stateDetail?.title) {
        seoDataCustom.title = stateDetail?.title;
      }

      if (stateDetail?.shortDescription) {
        seoDataCustom.description = stateDetail?.shortDescription;
      }
      return seoDataCustom;
    },
    [stateDetail, statePage]
  );

  const pageBlocks = useMemo(() => statePage?.blocks, [statePage]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isErrorDetail) {
    return <NotFound />;
  }

  return (
    <div className="u-pt-32 u-pt-md-32 u-pb-32 u-pb-md-80 u-pb-lg-100">

      <HelmetContainer
        ogData={{
          heading: pageBlocks?.openGraph?.heading || '',
          description: pageBlocks?.openGraph?.description || '',
          type: pageBlocks?.openGraph?.type || '',
          image: {
            path: detail.srcImage,
            width: '600',
            height: '315'
          },
        }}
        seoData={seoData}
      >
        {stateDetail?.updatedAt && <meta property="og:updated_time" content={stateDetail.updatedAt} />}
        {stateDetail?.updatedAt && <meta property="og:modified_time" content={stateDetail.updatedAt} />}
        {stateDetail?.publishedAt && <meta property="og:published_time" content={stateDetail.publishedAt} />}
      </HelmetContainer>

      <section>
        <Container>
          <Breadcrumb items={fullBreadcrumbs} />
          <div className="p-newsDetail_postWrapper">

            {/* {hashtagList && hashtagList.length > 0 && (
              <div className="p-newsDetail_side">

                <div className="p-newsDetail_hashtagContainer">
                  <Paragraph.Text
                    content={pageBlocks?.sidebar.tileKeyword}
                    modifiers={['600', 'raisinBlack']}
                    size={{
                      fontSize: '14',
                      lineHeight: '17',
                    }}
                  />

                  <div className="p-newsDetail_hashtagList">
                    {hashtagList.map((item) => (
                      <div key={item.id} className="p-newsDetail_hashtag">
                        <Link href={PATHS.NEWS_BY_HASHTAG_WITH_SLUG(item.slug)}>
                          <Paragraph.Text
                            content={item.title}
                            size={{
                              fontSize: '14',
                              lineHeight: '17'
                            }}
                          />
                        </Link>
                      </div>
                    ))}
                  </div>

                </div>

              </div>
            )} */}

            <div className="p-newsDetail_post">
              <Paragraph.Heading
                type="h1"
                content={detail?.title}
                size={{
                  fontSize: '40',
                  lineHeight: '48'
                }}
                sizeTablet={{
                  fontSize: '40',
                  lineHeight: '48'
                }}
                sizeMobile={{
                  fontSize: '32',
                  lineHeight: '40'
                }}
                modifiers={['raisinBlack', '700', 'center']}
              />
              <div style={{ minWidth: '352px' }} className="d-flex gap-1 u-mt-12 align-items-center">
                <Icon iconName="calendar_days" iconSize="20" />
                <Paragraph.Text
                  content={detail?.time}
                  modifiers={['raisinBlack07']}
                  size={{
                    fontSize: '16',
                    lineHeight: '0'
                  }}
                />
                <Link href={LINK_GOOGLE_NEWS}>
                  <Image src={URL_IMAGE_GOOGLE_NEWS} width={250} height={30} />
                </Link>
              </div>
              {/* <div className="u-mt-16 m-mt-lg-32">
                <Image size="contain" ratio="432x224" src={detail?.srcImage} alt={detail?.title} />
              </div> */}
              <div className="u-mt-16 p-newsDetail_post_content">
                <Paragraph.Text
                  type="div"
                  size={{
                    fontSize: '16',
                    lineHeight: '19'
                  }}
                  modifiers={['raisinBlack']}
                  content={
                    detail?.content
                  }
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {
        listRelated.length > 0 && (
          <LazyLoad height={400} once>
            <section className="u-mt-48 u-mt-md-60 u-mt-lg-80">
              <Container>
                <div className="text-md-center">
                  <Paragraph.Heading
                    type="h4"
                    content={pageBlocks?.relatedPostsHotProducts.titleRelatedPosts}
                    modifiers={['700', 'raisinBlack']}
                    size={{
                      fontSize: '36',
                      lineHeight: '43'
                    }}
                    sizeTablet={{
                      fontSize: '24',
                      lineHeight: '29'
                    }}
                  />
                </div>
                <Row className="u-mt-md-6">
                  {listRelated?.map((item, index) => (
                    <Col
                      className="u-mt-18 u-mt-md-20 u-mt-lg-32"
                      md={6}
                      lg={4}
                      key={`card-related-${index.toString()}`}
                    >
                      <NewsCard
                        {...item}
                        ratio="432x224"
                      />
                    </Col>
                  ))}
                </Row>
              </Container>
            </section>
          </LazyLoad>
        )
      }

      <LazyLoad height={800} once offset={1000}>
        <Products title={pageBlocks?.relatedPostsHotProducts.titleHotProducts} />
      </LazyLoad>

    </div>
  );
};

export default Screen;
